import React from 'react';
import DOMPurify from 'dompurify';
import regexMap from '../constants/regex-map';


/**
 * Check for all supported markdown formats: link, list, and bold/italic
 * @param  {String} message - String that needs parsing
 * @param  {Object} props - object containing optional fields:
 * { className, linkClasses, liClasses, tagName, ulClasses }
 * @return {React Element || null}
 */
export function parseMarkdown(message, props) {
  const {
    className,
    id,
    language,
    linkClasses,
    liClasses,
    tagName,
    storefront,
    ulClasses,
    showRadio,
  } = props;

  if (regexMap.MARKDOWN_URL.test(message)) {
    return parseURL(
      message,
      id,
      className,
      language,
      linkClasses,
      storefront,
      tagName,
    );
  } else if (regexMap.MARKDOWN_LIST.test(message)) {
    if (showRadio) {
      return parseListRadio(
        message,
        id,
        ulClasses,
        liClasses,
      );
    } else {
      return parseList(
        message,
        id,
        ulClasses,
        liClasses,
      );
    }
  } else if (regexMap.MARKDOWN_BOLD.test(message) || regexMap.MARKDOWN_ITALIC.test(message)) {
    return parseBoldAndItalic(
      message,
      id,
      true,
      className,
      tagName,
    );
  } else {
    return false;
  }
}

/**
 * Create <a> tag from string.
 * @param  {String} message - String that needs parsing
 * @param  {String} className - Class for element
 * @param  {String} linkClasses - Class for <a> tag
 * @param  {String} tagName - Type of tag
 * @return {React Element}
 */
export function parseURL(
  message,
  id,
  className = '',
  language,
  linkClasses = '',
  storefront,
  tagName = 'span',
) {
  const mdurl = regexMap.MARKDOWN_URL;
  const links = message.match(mdurl);

  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if ('target' in node) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener');
    }
  });

  const sanitize = DOMPurify.sanitize;

  if (links) {
    links.forEach((link) => {
      const mdArray = link.split(mdurl);
      const mdCopy = mdArray[1];
      const mdUrl = mdArray[2];

      const isAbsolute = regexMap.IS_ABSOLUTE_URL.test(mdUrl);
      const isLinkToCmsFile = regexMap.URL_LINK_TO_CMS_FILE.test(mdUrl);
      const splitStorefront = storefront.split('-');
      let newStorefront = storefront;

      if (splitStorefront && splitStorefront.length && splitStorefront.length === 2) {
        newStorefront = splitStorefront?.[1] || storefront;
      }
      const url = isAbsolute || isLinkToCmsFile ? mdUrl : `/${language}-${newStorefront}${mdUrl}`;

      const anchorElement =
        `<a href=${url} target="_blank" class="${linkClasses}">
          ${mdCopy}
        </a>`;

      message = message.replace(link, anchorElement);
    });
  }

  return React.createElement(tagName, {
    className: className,
    id: id,
    dangerouslySetInnerHTML: {
      __html: sanitize(message),
    },
  });
}

/**
 * Create list from string with <ul> and <li> tags
 * @param  {String} message - String that needs parsing
 * @param  {String} ulClasses - Class for <ul> element
 * @param  {String} liClasses - Class for <li> elements
 * @return {React Element}
 */
export function parseList(message, id, ulClasses = '', liClasses = '') {
  const mdlist = regexMap.MARKDOWN_LIST;
  const list = message.match(mdlist);
  const sanitize = DOMPurify.sanitize;

  list.forEach((listItem, i) => {
    listItem = listItem.replace('*', '');
    message = message.replace(list[i], `<li><p class=${liClasses}>${listItem}</p></li>`);
  });


  if (message) {
    return React.createElement('ul', {
      className: ulClasses,
      id: id,
      dangerouslySetInnerHTML: {
        __html: sanitize(message),
      },
    });
  }
}

/**
 * Create list from string with <ul> and <li> tags adding a radiobutton
 * @param  {String} message - String that needs parsing
 * @param  {String} ulClasses - Class for <ul> element
 * @param  {String} liClasses - Class for <li> elements
 * @return {React Element}
 */
export function parseListRadio(message, id, ulClasses = '', liClasses = '') {
  const mdlist = regexMap.MARKDOWN_LIST;
  const list = message.match(mdlist);
  const sanitize = DOMPurify.sanitize;

  list.forEach((listItem, i) => {
    listItem = listItem.replace('*', '');
    message = message.replace(list[i], `<li class='CubaDestinationModal-listIcon'><input type='radio' name='cubaOption' value='${listItem}' required='true'><p class=${liClasses}>${listItem}</p></li>`);
  });
  if (message) {
    return React.createElement('ul', {
      className: ulClasses,
      id: id,
      dangerouslySetInnerHTML: {
        __html: sanitize(message),
      },
    });
  }
}

/**
 * Takes text and adds <strong> and <em> elements
 * @param  {String} message - String that needs parsing
 * @param  {Boolean} returnElement - Should function return element or string
 * @param  {String} elementClasses - Class for returned element
 * @param  {String} tagName - Tag for the element
 * @return {React Element} or {String}
 */
export function parseBoldAndItalic(message, id, returnElement = true, elementClasses = '', tagName = 'span') {
  const boldItems = message.match(regexMap.MARKDOWN_BOLD);
  const sanitize = DOMPurify.sanitize;

  if (boldItems) {
    boldItems.forEach((boldItem, i) => {
      boldItem = boldItem.replace(/\*/g, '');
      message = message.replace(boldItems[i], `<strong>${boldItem}</strong>`);
    });
  }

  const italicItems = message.match(regexMap.MARKDOWN_ITALIC);

  if (italicItems) {
    italicItems.forEach((italicItem, i) => {
      italicItem = italicItem.replace(/\*/g, '');
      message = message.replace(italicItems[i], `<em>${italicItem}</em>`);
    });
  }

  if (returnElement) {
    return React.createElement(tagName, {
      className: elementClasses,
      id: id,
      dangerouslySetInnerHTML: {
        __html: sanitize(message),
      },
    });
  } else {
    return message;
  }
}
